/**
 * Staff: who in each division (Discord league) still has to link their accounts to the site. Not signed in at all,
 * signed in with Discord but no Showdown name (Ready battles and voice need one), or linked. Each league has a
 * reminder to copy into its Discord channel, with an @mention for everyone not done. Global Admins can also set up
 * someone who can't use Discord, to sign in with their Showdown name.
 */
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { useMe } from '../me';
import { Link } from '../router';
import { ErrorBox, Loading, toast } from '../ui';

interface LinkingPlayer {
	name: string;
	discordId: string;
	signedIn: boolean;
	discordHandle: string | null;
	showdown: string | null;
	/** A Global Admin let them sign in with their Showdown name */
	showdownSignIn: boolean;
}
interface LinkingLeague {
	id: number;
	name: string;
	status: string;
	pools: { label: string, players: LinkingPlayer[] }[];
}

const SITE = 'https://draft.ogreclan.com';

/** Not done first: not signed in, then signed in with no Showdown name */
const order = (p: LinkingPlayer) => p.showdown ? 2 : p.signedIn ? 1 : 0;

function Status({ player }: { player: LinkingPlayer }) {
	if (player.showdown) {
		return <><span class="badge good">Linked as {player.showdown}</span>
			{player.showdownSignIn && <> <span class="badge accent">Signs in with Showdown</span></>}</>;
	}
	if (player.signedIn) return <span class="badge warn">No Showdown name</span>;
	return <span class="badge bad">Not signed in</span>;
}

function reminder(league: LinkingLeague, todo: LinkingPlayer[]) {
	return `${todo.map(p => `<@${p.discordId}>`).join(' ')}\n` +
		`Please link your accounts for ${league.name}: sign in at ${SITE} with Discord, then press your name (top right), ` +
		`**Link my Showdown name**, and type the command it shows in any chat on https://play.ogreclan.com. ` +
		`It's needed for Ready battles and voice.`;
}

/** Global Admins: for a player who can't use Discord, link their Showdown name and let them sign in with it */
async function setUpWithoutDiscord(player: LinkingPlayer, reload: () => Promise<void>, move = false): Promise<void> {
	const name = prompt(`${player.name} can't use Discord? Type their Showdown name (a registered one). It's linked to them, and they ` +
		`sign in by typing /ogreclan signin on play.ogreclan.com as that name.`)?.trim();
	if (!name) return;
	try {
		await api.post('/api/admin/linking/without-discord', { discordId: player.discordId, name, move });
		toast(`${player.name} is linked as ${name}, and can sign in with it: /ogreclan signin on play.ogreclan.com.`, 'good');
		await reload();
	} catch (error) {
		if (error instanceof ApiError && error.status === 409 && !move && confirm(`${error.message} Move it to ${player.name} instead?`)) {
			await api.post('/api/admin/linking/without-discord', { discordId: player.discordId, name, move: true })
				.then(() => reload(), (e: Error) => toast(e.message, 'bad'));
			return;
		}
		toast((error as Error).message, 'bad');
	}
}

function LeagueLinking({ league, everyone, globalAdmin, reload }: { league: LinkingLeague, everyone: boolean, globalAdmin: boolean, reload: () => Promise<void> }) {
	const all = league.pools.flatMap(pool => pool.players);
	const todo = all.filter(p => !p.showdown);
	const notSignedIn = all.filter(p => !p.signedIn).length;
	return <div class="card">
		<div class="row" style="justify-content:space-between">
			<h2 style="margin:0"><Link href={`/discord/${league.id}`}>{league.name}</Link></h2>
			{todo.length > 0 && <button type="button" class="small" onClick={() =>
				void navigator.clipboard?.writeText(reminder(league, todo)).then(() => toast('Copied: paste it in the league\'s Discord channel.'))}>
				Copy reminder</button>}
		</div>
		<p class="muted small">
			{all.length - todo.length} of {all.length} linked
			{notSignedIn > 0 && <> · {notSignedIn} not signed in</>}
			{todo.length - notSignedIn > 0 && <> · {todo.length - notSignedIn} with no Showdown name</>}
		</p>
		{!everyone && !todo.length ? <p class="notice good">Everyone has linked.</p> : league.pools.map(pool => {
			const shown = [...pool.players].filter(p => everyone || !p.showdown).sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
			if (!shown.length) return null;
			return <div key={pool.label}>
				{league.pools.length > 1 && <h3>Pool {pool.label}</h3>}
				<div class="table-wrap"><table>
					<thead><tr><th>Player</th><th>Discord</th><th>Linking</th>{globalAdmin && <th />}</tr></thead>
					<tbody>{shown.map(p => <tr key={p.discordId}>
						<td>{p.name}</td>
						<td class="small">{p.discordHandle ? `@${p.discordHandle}` : <span class="muted">ID {p.discordId}</span>}</td>
						<td><Status player={p} /></td>
						{globalAdmin && <td class="right nowrap">{!p.showdown && <button type="button" class="small"
							onClick={() => void setUpWithoutDiscord(p, reload)}>Set up without Discord</button>}</td>}
					</tr>)}</tbody>
				</table></div>
			</div>;
		})}
	</div>;
}

export function LinkingPage() {
	const me = useMe();
	const staff = !!(me?.user?.isAdmin || me?.user?.isModerator);
	const [leagues, setLeagues] = useState<LinkingLeague[] | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [everyone, setEveryone] = useState(false);
	const load = () => api.get<{ leagues: LinkingLeague[] }>('/api/admin/linking').then(r => setLeagues(r.leagues), setError);
	useEffect(() => {
		if (staff) void load();
	}, [staff]);

	if (!me) return <Loading />;
	if (!staff) return <p class="notice bad">Only the site's admins and draft moderators can see this page.</p>;
	if (error) return <ErrorBox error={error} />;
	if (!leagues) return <Loading />;
	return <>
		<h1>Linking</h1>
		<p class="muted">Who in each division still has to link their accounts: sign in with Discord, then link their Showdown name
			(Ready battles and voice need it). <strong>Copy reminder</strong> copies an @mention for everyone not done, with how to do
			it, to paste in the league's Discord channel.</p>
		{me.user!.isGlobalAdmin && <p class="muted small">Someone who can't use Discord (a lost password, say): <strong>Set up without
			Discord</strong> links the Showdown name you type to them and lets them sign in with it, by typing <code>/ogreclan
			signin</code> on play.ogreclan.com as that name. Only do it for the person who owns that name.</p>}
		<label class="row small"><input type="checkbox" checked={everyone} onChange={e => setEveryone((e.target as HTMLInputElement).checked)} />
			Show everyone, linked too</label>
		{leagues.length ? leagues.map(league => <LeagueLinking key={league.id} league={league} everyone={everyone}
			globalAdmin={!!me.user!.isGlobalAdmin} reload={load} />) :
			<p class="empty">No Discord leagues running.</p>}
	</>;
}
